// Scroll management utilities for the Borrower Portal

/**
 * Resets scroll position for all scrollable containers
 * @param {Object} options - Configuration options
 * @param {boolean} options.animate - Whether to animate the scroll reset
 * @param {number} options.duration - Animation duration in ms (if animate is true)
 */
export function resetScrollPosition(options = { animate: false, duration: 300 }) {
  const scrollableContainers = [
    window,
    document.documentElement,
    document.body,
    ...Array.from(document.querySelectorAll('.content, .main-panel, .wrapper, .container-iframe, .task-container')),
  ];

  const scrollToTop = (element) => {
    if (!element) return;

    if (options.animate) {
      const start = element.scrollTop || window.pageYOffset;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / options.duration, 1);

        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const newPosition = start * (1 - easeOutCubic);

        if (element === window) {
          window.scrollTo(0, newPosition);
        } else {
          element.scrollTop = newPosition;
        }

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    } else {
      if (element === window) {
        window.scrollTo(0, 0);
      } else {
        element.scrollTop = 0;
      }
    }
  };

  scrollableContainers.forEach(scrollToTop);
}

/**
 * Creates a scroll reset observer for a specific container
 * @param {HTMLElement} container - The container to observe
 * @param {Function} callback - Callback to execute when scroll reset is needed
 */
export function createScrollResetObserver(container, callback) {
  if (!container) return null;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' || mutation.type === 'attributes') {
        callback();
      }
    });
  });

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  return observer;
}

/**
 * Handles scroll reset when transitioning between views
 * @param {string} fromView - The view being transitioned from
 * @param {string} toView - The view being transitioned to
 */
export function handleViewTransition(fromView, toView) {
  if (fromView === 'document-review') {
    resetScrollPosition({ animate: true, duration: 300 });
  }

  setTimeout(() => {
    resetScrollPosition({ animate: false });
  }, 100);
}

/**
 * Initializes scroll management for the application
 */
export function initializeScrollManagement() {
  window.addEventListener('popstate', () => {
    resetScrollPosition({ animate: true, duration: 300 });
  });

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      resetScrollPosition({ animate: false });
    }, 150);
  });
}
